// Industries Page Data

export const industriesOverview = [
  {
    title: "Healthcare & Life Sciences",
    tagline: "Voice agents that complete clinical and administrative workflows.",
    description: "From benefit verification to prior authorization, Vedara agents navigate complex payer interactions and update enterprise systems.",
    path: "/industries/healthcare",
    workflows: ["Benefit Verification", "Prior Authorization", "Referral Management", "Patient Scheduling"]
  },
  {
    title: "Insurance",
    tagline: "Autonomous claims processing and policy servicing.",
    description: "Voice agents that handle claims intake, status updates, and policy changes while maintaining context across multi-day workflows.",
    path: "/industries/insurance",
    workflows: ["Claims Intake", "Claims Status", "Policy Servicing", "Underwriting Operations"]
  },
  {
    title: "Financial Services",
    tagline: "Compliant voice workflows for lending and servicing.",
    description: "Execute loan processing, verification, and collections workflows with full regulatory compliance and audit trails.",
    path: "/industries/financial-services",
    workflows: ["Loan Processing", "Customer Verification", "Collections", "Account Servicing"]
  },
  {
    title: "Enterprise",
    tagline: "Voice agents for operations, sales, and support.",
    description: "Automate customer onboarding, vendor management, and back-office operations with persistent, long-horizon agents.",
    path: "/industries/enterprise",
    workflows: ["Customer Onboarding", "Vendor Management", "Appointment Scheduling", "Sales Development"]
  }
];

export const healthcareUseCases = [
  {
    category: "Payers",
    title: "Benefit Verification Agent",
    problem: "Manual benefit verification takes 20-45 minutes per case. Staff must call payers, navigate IVRs, wait on hold, document conversations, and manually enter data into EHR systems.",
    solution: "Vedara's Benefit Verification Agent makes outbound calls to payers, navigates IVR systems, speaks with representatives, extracts coverage information, validates authorization requirements, and writes structured results directly into the EHR.",
    workflow: [
      "Receives verification request from EHR",
      "Calls payer automatically",
      "Navigates IVR using DTMF or speech",
      "Converses with payer representative",
      "Extracts: Coverage status, deductibles, copays, authorization requirements",
      "Validates information completeness",
      "Updates EHR with structured data",
      "Escalates exceptions to staff"
    ],
    outcomes: [
      "15-20 minute time savings per verification",
      "95%+ accuracy in data capture",
      "Same-day verification completion",
      "Reduced claim denials from incomplete verification"
    ],
    technicalDepth: "Integrates with: Epic, Cerner, Athena, Allscripts. Handles: Multi-payer environments, Medicare/Medicaid/Commercial plans, Real-time eligibility checks (270/271 transactions), IVR navigation with speech recognition."
  },
  {
    category: "Payers", 
    title: "Prior Authorization Agent",
    problem: "Prior authorization workflows span days or weeks. Staff submit requests, wait for responses, follow up on missing information, and chase down approvals across fragmented systems.",
    solution: "A long-horizon agent that initiates authorization requests, monitors status, follows up on missing documentation, escalates denials, and maintains state until final approval.",
    workflow: [
      "Submits authorization request to payer",
      "Monitors authorization portal for updates",
      "Calls payer if status doesn't update within SLA",
      "Requests missing clinical documentation",
      "Coordinates with provider for additional information",
      "Escalates denials with supporting documentation",
      "Updates provider and patient on status changes",
      "Closes workflow when authorization is approved"
    ],
    outcomes: [
      "3-5 day reduction in authorization cycle time",
      "Elimination of lost or forgotten follow-ups",
      "Automated status updates to providers",
      "Higher first-pass approval rates"
    ],
    technicalDepth: "Maintains persistent state across 1-14 day workflows. Integrates with payer portals, EHRs, and fax systems. Handles: Authorization status tracking, Document submission, Appeal workflows, Multi-party coordination."
  },
  {
    category: "Providers",
    title: "Patient Scheduling Agent",
    problem: "Patients call to schedule appointments. Staff must check provider availability, verify insurance, coordinate referrals, send confirmations, and handle rescheduling.",
    solution: "Voice agent handles inbound and outbound scheduling calls, checks real-time availability, verifies coverage, books appointments, sends confirmations, and manages rescheduling workflows.",
    workflow: [
      "Answers patient call or makes outbound call",
      "Understands scheduling request and urgency",
      "Checks provider availability in real-time",
      "Verifies insurance coverage for visit type",
      "Books appointment in scheduling system",
      "Sends confirmation via SMS/email",
      "Handles pre-visit requirements (forms, labs)",
      "Makes reminder calls 48 hours before visit"
    ],
    outcomes: [
      "24/7 scheduling availability",
      "Reduced no-show rates via automated reminders",
      "Higher patient satisfaction scores",
      "Staff capacity redirected to complex cases"
    ],
    technicalDepth: "Integrates with: Practice management systems, EHRs, Patient portals. Handles: Real-time availability checks, Insurance verification, Multi-location scheduling, Waitlist management."
  },
  {
    category: "Revenue Cycle",
    title: "Claims Status Agent",
    problem: "Providers call payers daily to check claim status. Each call takes 10-15 minutes navigating IVRs and speaking with representatives.",
    solution: "Agent makes systematic outbound calls to payers, retrieves claim status, identifies denial reasons, and updates billing systems with current status.",
    workflow: [
      "Pulls list of outstanding claims from billing system",
      "Calls payers automatically based on claim age",
      "Navigates payer IVR systems",
      "Retrieves claim status and payment information",
      "Identifies denial or rejection reasons",
      "Documents status in billing system",
      "Escalates denials requiring appeal",
      "Follows up on resubmissions"
    ],
    outcomes: [
      "10-15 minute savings per claim inquiry",
      "Systematic follow-up on all aging claims",
      "Faster identification of denial reasons",
      "Reduced days in A/R"
    ],
    technicalDepth: "Integrates with billing systems, clearinghouses, and payer portals. Handles: EDI 276/277 transactions, Payer-specific IVR navigation, Denial code interpretation, Resubmission workflows."
  }
];

export const insuranceUseCases = [
  {
    category: "Claims",
    title: "First Notice of Loss (FNOL) Agent",
    problem: "Policyholders call to report claims. Intake requires gathering incident details, policy information, involved parties, and initial damage assessment.",
    solution: "Voice agent handles inbound FNOL calls, gathers structured claim information, validates policy coverage, assigns claim number, and routes to appropriate adjuster.",
    workflow: [
      "Answers policyholder call",
      "Verifies caller identity and policy status",
      "Gathers: Incident date/time/location, Description of loss, Involved parties, Initial damage estimate",
      "Validates coverage for incident type",
      "Creates claim in claims system",
      "Assigns claim number and adjuster",
      "Schedules inspection if required",
      "Sends confirmation and next steps"
    ],
    outcomes: [
      "24/7 claims intake availability",
      "Consistent data capture across all claims",
      "Immediate claim number assignment",
      "Faster time to adjuster assignment"
    ],
    technicalDepth: "Integrates with policy administration systems, claims management platforms. Handles: Multi-line claims (auto, property, liability), Catastrophe event surges, Multi-party incidents, Photo/video upload coordination."
  },
  {
    category: "Policy Servicing",
    title: "Policy Change Agent",
    problem: "Policyholders call to make changes: add drivers, update addresses, adjust coverage. Each change requires validation, underwriting review, premium recalculation, and documentation.",
    solution: "Agent handles policy change requests, validates eligibility, submits for underwriting if required, calculates premium impact, and updates policy records.",
    workflow: [
      "Receives policy change request",
      "Validates policyholder identity",
      "Confirms requested changes",
      "Checks underwriting rules for change type",
      "Submits for underwriting approval if required",
      "Calculates premium impact",
      "Presents options to policyholder",
      "Processes approved changes",
      "Issues updated policy documents"
    ],
    outcomes: [
      "Same-call policy changes for eligible requests",
      "Reduced underwriting queue for simple changes",
      "Improved customer satisfaction",
      "Lower policy cancellation rates"
    ],
    technicalDepth: "Integrates with policy admin systems, rating engines, underwriting platforms. Handles: Multi-line policy changes, Premium recalculation, Document generation, Compliance validation."
  }
];

export const financialServicesUseCases = [
  {
    category: "Lending",
    title: "Loan Application Follow-up Agent",
    problem: "Incomplete loan applications stall in the pipeline. Loan officers must call applicants repeatedly to request missing documents, answer questions, and move applications forward.",
    solution: "Agent makes systematic outbound calls to applicants, identifies missing items, answers questions, coordinates document submission, and updates loan origination system.",
    workflow: [
      "Identifies incomplete applications in LOS",
      "Calls applicant automatically",
      "Explains missing documentation requirements",
      "Answers questions about requirements",
      "Coordinates document upload or fax",
      "Verifies document receipt",
      "Updates application status",
      "Escalates to loan officer if needed"
    ],
    outcomes: [
      "Reduced application abandonment rates",
      "Faster time to loan decision",
      "Loan officer capacity for complex cases",
      "Higher application completion rates"
    ],
    technicalDepth: "Integrates with loan origination systems, document management platforms. Handles: Consumer loans, mortgages, commercial loans. Regulatory compliance: TILA, RESPA, ECOA documentation requirements."
  },
  {
    category: "Collections",
    title: "Early-Stage Collections Agent",
    problem: "Accounts become delinquent. Collections teams must call borrowers, understand payment challenges, negotiate payment arrangements, and process payments.",
    solution: "Agent makes compliant outbound collections calls, discusses account status, offers payment options, processes payments, and documents interactions.",
    workflow: [
      "Identifies accounts in early delinquency",
      "Makes outbound calls (TCPA compliant)",
      "Verifies borrower identity",
      "Discusses account status and payment options",
      "Offers: Full payment, Partial payment, Payment plan",
      "Processes payment via phone or coordinates online payment",
      "Documents conversation and outcome",
      "Schedules follow-up if payment plan established"
    ],
    outcomes: [
      "Higher right-party contact rates",
      "Improved cure rates in early delinquency",
      "Consistent FDCPA compliance",
      "Reduced cost per collection"
    ],
    technicalDepth: "Regulatory compliance: FDCPA, TCPA, state-specific collection laws. Integrates with: Loan servicing platforms, Payment processors, Dialer systems. Handles: Call time restrictions, Cease and desist requests, Dispute handling."
  }
];

export const enterpriseUseCases = [
  {
    category: "Sales",
    title: "Lead Qualification Agent",
    problem: "Sales teams receive hundreds of inbound leads. SDRs must call each lead, qualify interest, gather requirements, and route to appropriate sales rep.",
    solution: "Agent makes systematic outbound calls to leads, qualifies interest and fit, gathers requirements, schedules meetings with sales reps, and updates CRM.",
    workflow: [
      "Receives new lead from CRM",
      "Makes outbound call within defined SLA",
      "Confirms contact information",
      "Qualifies: Budget, Authority, Need, Timeline",
      "Gathers use case and requirements",
      "Scores lead based on qualification criteria",
      "Schedules meeting with sales rep if qualified",
      "Updates CRM with qualification data",
      "Routes to nurture campaign if not ready"
    ],
    outcomes: [
      "Faster lead response time",
      "Consistent qualification criteria",
      "Higher sales rep meeting efficiency",
      "Improved lead-to-opportunity conversion"
    ],
    technicalDepth: "Integrates with: Salesforce, HubSpot, Outreach, Calendaring systems. Handles: Multi-touch follow-up sequences, Lead scoring automation, Calendar coordination, CRM data enrichment."
  },
  {
    category: "Operations",
    title: "Vendor Onboarding Agent",
    problem: "New vendor onboarding requires: W-9 collection, insurance verification, contract execution, system access setup, and compliance documentation.",
    solution: "Agent coordinates multi-step vendor onboarding, tracks document submission, follows up on missing items, and maintains workflow state until completion.",
    workflow: [
      "Receives new vendor request",
      "Calls vendor contact",
      "Explains onboarding requirements",
      "Requests: W-9, Insurance certificates, Banking information, References",
      "Tracks document submission",
      "Follows up on missing items",
      "Coordinates contract execution",
      "Updates vendor master in ERP",
      "Notifies procurement when vendor is active"
    ],
    outcomes: [
      "Reduced vendor onboarding time",
      "Complete vendor documentation",
      "Automated follow-up on missing items",
      "Procurement capacity for strategic sourcing"
    ],
    technicalDepth: "Integrates with: ERP systems, Contract management platforms, Document repositories. Handles: Multi-party coordination, Compliance validation, Insurance verification, Tax documentation."
  }
];

export const industryStats = {
  healthcare: {
    stat1: { value: "20-45 min", label: "Average time saved per benefit verification" },
    stat2: { value: "3-5 days", label: "Reduction in prior auth cycle time" },
    stat3: { value: "95%+", label: "Data capture accuracy" }
  },
  insurance: {
    stat1: { value: "24/7", label: "Claims intake availability" },
    stat2: { value: "Same-day", label: "Policy change processing" },
    stat3: { value: "40%+", label: "Reduction in handling time" }
  },
  financial: {
    stat1: { value: "30%+", label: "Improvement in cure rates" },
    stat2: { value: "50%+", label: "Faster application completion" },
    stat3: { value: "100%", label: "Regulatory compliance" }
  },
  enterprise: {
    stat1: { value: "<5 min", label: "Lead response time" },
    stat2: { value: "60%+", label: "Reduction in onboarding time" },
    stat3: { value: "3-5x", label: "Increase in SDR capacity" }
  }
};
